import { connect, signMessage } from '@wagmi/core'
import { config, metadata } from '../reown.ts'
import { callApi } from '@/services/call-api.ts'
import { apiToken, isWalletConnected, loginProvider, referrerCode, walletConnected } from '@/composables/auth.ts'
import { watch } from 'vue'
// import posthog from 'posthog-js'
import { coinbaseWallet } from '@wagmi/connectors'
import { type Event, useAppKit, useAppKitAccount, useAppKitEvents, useDisconnect } from '@reown/appkit/vue'
import flagsmith from 'flagsmith'
import mixpanel from 'mixpanel-browser'
import { trackWalletConnected } from '@/lib/mixpanel.ts'
import { useUpdateEmail } from '@/composables/update-email'

export const useWalletAuth = () => {
  const account = useAppKitAccount()
  const { updateEmail, currentEmail } = useUpdateEmail()

  async function authenticate(event?: Event) {
    const { message, nonce } = await callApi<{ message: string, nonce: string }>({
      path: '/api/auth/signature/message',
      method: 'POST',
      body: { message: 'Please sign this message to login to Lingo!', walletAddress: account.value.address },
    })
    const signature = await signMessage(config, {
      message: message,
    })
    const { token } = await callApi<{ token: string }>({
      path: '/api/auth/signature/verify',
      method: 'POST',
      body: { nonce, signature, referrerCode: referrerCode.value },
    })
    apiToken.value = token
    walletConnected.value = account.value.address
    trackWalletConnected(
      walletConnected.value,
      event as Extract<Event, { properties: { name: string } }>,
    )
    await flagsmith.identify(walletConnected.value)
    const userEmail = account.value.embeddedWalletInfo?.user?.email
    if (!currentEmail && userEmail) {
      await updateEmail(userEmail)
    }
  }

  async function connectAndAuthenticate() {
    mixpanel.track('Wallet Connect Started')
    if (account.value.address) {
      await authenticate()
      return
    }
    const { open } = useAppKit()
    await open({ view: 'Connect' })
    const events = useAppKitEvents()
    const unwatch = watch(events, events => {
      const eventName = events.data.event
      // save the event provider as the login can be broken by the user
      // and the provider is not saved once the account is connected
      switch (eventName) {
      case 'SOCIAL_LOGIN_STARTED':
        loginProvider.value = events.data.properties.provider
        break
      case 'EMAIL_LOGIN_SELECTED':
        loginProvider.value = 'email'
        break
      case 'SELECT_WALLET':
        loginProvider.value = events.data.properties.name
        break
      }

      if (eventName === 'CONNECT_SUCCESS' || eventName === 'SOCIAL_LOGIN_SUCCESS') {
        unwatch()
        authenticate(events.data)
      }
      if (eventName === 'EMAIL_VERIFICATION_CODE_PASS') {
        unwatch()
        const unwatchAddress = watch(() => account.value.isConnected, async (value) => {
          if (value) {
            unwatchAddress()
            setTimeout(async () => {
              await authenticate(events.data)
            }, 1000)
          }
        })
      }
    })
  }

  const { disconnect } = useDisconnect()
  async function logout() {
    try {
      await disconnect()
    } catch (e) {
      // In case wallet is not connected, it will throw an error
      console.log('error:', e)
    }
    apiToken.value = ''
    walletConnected.value = ''
    await flagsmith.logout()
  }

  async function createSmartWallet (){
    await connect(config, { connector: coinbaseWallet({
      appName: metadata?.name ?? 'Unknown',
      appLogoUrl: metadata?.icons[0] ?? 'Unknown',
      enableMobileWalletLink: true,
    }) })
    await authenticate()
  }

  watch(() => account.value.address, async (value) => {
    if (isWalletConnected.value && !!value && value !== walletConnected.value) {
      await logout()
    }
  })

  return {
    login: connectAndAuthenticate,
    logout: logout,
    createSmartWallet,
  }
}
